import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Music4, UploadCloud, ScanSearch, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { OrganizerOverview } from "@/components/musiclib/OrganizerOverview";
import { MusicLibraryTable } from "@/components/musiclib/MusicLibraryTable";
import { ImportModal } from "@/components/musiclib/ImportModal";
import {
  DecadesPanel, GenresPanel, DuplicatesPanel, StationsPanel, ExportPanel, DevStatus,
} from "@/components/musiclib/OrganizerPanels";
import { AutoTagPanel } from "@/components/musiclib/AutoTagPanel";
import { AnalysisPanel } from "@/components/musiclib/AnalysisPanel";

const TABS = [
  ["overview", "Overview"], ["library", "Music Library"], ["autotag", "Auto Tagging"],
  ["analysis", "Audio Analysis"], ["genres", "Genre Organizer"], ["decades", "Decades"],
  ["tophits", "Top Hits"], ["hiddengems", "Hidden Gems"], ["duplicates", "Duplicates"],
  ["playlists", "Playlists"], ["stations", "Stations"], ["export", "Export"],
];

export default function MusicLibraryStudio() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [overview, setOverview] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [preset, setPreset] = useState({});
  const [scanning, setScanning] = useState(false);

  const loadOverview = useCallback(() => {
    api.get("/music/overview").then((r) => setOverview(r.data)).catch(() => setOverview(null));
  }, []);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  const setTab = (t) => setParams({ tab: t }, { replace: true });
  const openLibrary = (p = {}) => { setPreset({ ...p, _k: Date.now() }); setTab("library"); };

  const scan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/music/scan");
      setOverview(r.data);
      toast.success(`Library scanned — ${r.data.total_tracks} tracks, stats refreshed`);
    } catch { toast.error("Scan failed"); }
    setScanning(false);
  };

  return (
    <div className="space-y-5 px-3 pt-3 pb-6 md:p-0" data-testid="music-library-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shrink-0">
          <Music4 className="h-5 w-5 text-black" />
        </div>
        <div className="flex-1 min-w-56">
          <h1 className="type-section-heading text-ink-primary" style={{ fontSize: 20 }}>AI Music Library Organizer</h1>
          <p className="type-body text-ink-muted">Organize. Enrich. Categorize. Prepare your music for radio.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setImportOpen(true)} data-testid="ml-import-btn"
            className="type-button-label flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-2 text-bg-base hover:opacity-90">
            <UploadCloud size={14} /> Import Music
          </button>
          <button onClick={scan} disabled={scanning} data-testid="ml-scan-btn"
            className="type-button-label flex items-center gap-1.5 rounded-md border border-line px-3 py-2 text-ink-primary hover:bg-white/5 disabled:opacity-50">
            {scanning ? <Loader2 size={14} className="animate-spin" /> : <ScanSearch size={14} />} Scan Library
          </button>
          <button onClick={() => toast.info("Auto Tagging & Decades are live now — the one-click Auto Organize review screen arrives in Checkpoint 4")}
            data-testid="ml-auto-organize-btn"
            className="type-button-label flex items-center gap-1.5 rounded-md border border-sky-500/50 px-3 py-2 text-sky-300 hover:bg-sky-500/10">
            <Sparkles size={14} /> Auto Organize
            <span className="rounded-full border border-sky-500/40 px-1.5 text-[9px] font-mono">PHASE 2</span>
          </button>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-line pb-px" data-testid="ml-tabs">
        {TABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`ml-tab-${id}`}
            className={`whitespace-nowrap px-3 py-2 text-xs font-bold uppercase tracking-wider transition border-b-2 ${
              tab === id ? "border-brand-amber text-brand-amber" : "border-transparent text-ink-muted hover:text-ink-primary"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <OrganizerOverview overview={overview}
          onImport={() => setImportOpen(true)}
          onNavigate={setTab} onOpenLibrary={openLibrary} />
      )}
      {tab === "library" && <MusicLibraryTable preset={preset} onChanged={loadOverview} />}
      {tab === "genres" && <GenresPanel onPick={(g) => openLibrary({ genre: g })} />}
      {tab === "decades" && <DecadesPanel onPick={(d) => openLibrary({ decade: d })} />}
      {tab === "duplicates" && <DuplicatesPanel />}
      {tab === "stations" && <StationsPanel onPick={(s) => openLibrary({ station: s })} />}
      {tab === "export" && <ExportPanel />}
      {tab === "autotag" && <AutoTagPanel onOpenLibrary={openLibrary} onChanged={loadOverview} />}
      {tab === "analysis" && <AnalysisPanel onOpenLibrary={openLibrary} onChanged={loadOverview} />}
      {tab === "tophits" && (
        <DevStatus title="Top Hits" phase="Phase 2"
          description="Chart-position enrichment that surfaces the proven hits in your library per decade and genre."
          bullets={["Billboard/chart history matching", "Hit score per track", "Instant 'Top Hits' smart views"]} />
      )}
      {tab === "hiddengems" && (
        <DevStatus title="Hidden Gems" phase="Phase 2"
          description="Finds quality deep cuts you own but rarely rotate — perfect for specialty shows."
          bullets={["Low-rotation + high-quality detection", "Genre-balanced suggestions", "One-click add to rotation"]} />
      )}
      {tab === "playlists" && (
        <DevStatus title="Playlists — Smart Playlist Builder" phase="Phase 3"
          description="Build rule-based radio playlists (genre + decade + energy + rotation) and export them to your playout system."
          bullets={["Rule-based smart playlists", "Daypart templates", "Export to SAM / Myriad / RadioDJ", "Works with the Rotation Manager"]} />
      )}

      {importOpen && (
        <ImportModal onClose={() => setImportOpen(false)}
          onDone={() => { loadOverview(); setPreset((p) => ({ ...p, _k: Date.now() })); }} />
      )}
    </div>
  );
}
